/**
 * The shared frame of the two sign-in screens, in A2's language: canvas,
 * an `a-display` heading, the field on an `a-card`, and the actions pinned to
 * the bottom third.
 *
 * It is a `<form>`, so the keyboard's send key submits. The actions sit at
 * `mt-auto`: `interactive-widget=resizes-content` (index.html) shrinks the
 * viewport when the keyboard opens, so the primary button rides up above it
 * instead of hiding under it.
 *
 * On a desktop the column stays phone-width (420 px) and centres on the
 * canvas; the phone layout is never stretched.
 */
import { type FormEvent, type ReactNode, useRef } from "react";
import { useRevealOnKeyboard } from "../../ui/gesture";
import { useToastClearance } from "../../ui/overlay";

export interface AuthFrameProps {
  /** The primary button and the quieter ones under it. */
  actions: ReactNode;
  /** The field(s), painted on the card. */
  children: ReactNode;
  heading: ReactNode;
  lede: ReactNode;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

export function AuthFrame(props: AuthFrameProps) {
  // The PWA update prompt reaches sign-in by design (T46), and the toast
  // covered this button; it now clears the actions instead (T48). The column
  // can be taller than the viewport, which puts the actions below the fold —
  // `useToastClearance` clamps that at 0.
  const actions = useRef<HTMLDivElement>(null);
  useToastClearance(actions);
  // `mt-auto` is only enough while the column still FITS what the keyboard
  // leaves. It does on the owner's handset (691 px against 432) and it does
  // not on a short viewport — the container clips this button by 25 px, and
  // the scroll that would reach it is uncued (T122, measured T126). The
  // reveal is a no-op wherever the button is already visible, which is why it
  // costs the closed-keyboard layout nothing.
  useRevealOnKeyboard(actions);
  return (
    <form
      className="mx-auto flex min-h-full w-full max-w-105 flex-col px-4 pt-12 pb-6 lg:justify-center"
      noValidate
      onSubmit={props.onSubmit}
    >
      <header>
        <h1 className="a-display text-balance text-3xl text-ink">
          {props.heading}
        </h1>
        <p className="mt-3 text-base text-ink-2">{props.lede}</p>
      </header>
      <div className="a-card mt-8 p-4">{props.children}</div>
      <div className="mt-auto flex flex-col gap-2 pt-8 lg:mt-8" ref={actions}>
        {props.actions}
      </div>
    </form>
  );
}
